import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { activeDownloadsAtom } from './data/downloadQueue.atom';
import { useDownloadPoller } from './hooks/useDownloadPoller';
import {
  getActiveDownloadsCount,
  getIsExecutingDownload,
  initializeDownloadWindowState,
  setActiveDownloadsCount,
} from './utils/downloadWindowState';

import type { ReactNode } from 'react';

/**
 * Provider component that runs the global download polling mechanism
 * Mount this at the app root level inside JotaiProvider
 */
export const DownloadManagerProvider = ({ children }: { children: ReactNode }) => {
  useDownloadPoller();

  const activeDownloads = useAtomValue(activeDownloadsAtom);

  // Initialize window properties on mount
  useEffect(() => {
    initializeDownloadWindowState();
  }, []);

  // Sync active downloads count to window state for beforeunload handler
  useEffect(() => {
    setActiveDownloadsCount(activeDownloads.length);
  }, [activeDownloads.length]);

  // Warn users before leaving page when downloads are in progress
  // Uses window state for synchronous access without re-registering listener
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Allow navigation if actively executing a download
      if (getIsExecutingDownload()) {
        return;
      }

      // Prevent navigation if there are active downloads
      if (getActiveDownloadsCount() > 0) {
        // Browsers ignore custom messages and show their own warning dialog
        event.preventDefault();
        // oxlint-disable-next-line typescript/no-deprecated -- Chrome < 119 shows the dialog only when returnValue is set, and browserslist still includes Chrome 109
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return <>{children}</>;
};
